/**
 * Notifications Service - Sprint 19b
 *
 * API client for notifications endpoints. Routes are served by core-backend
 * (port 4050). Preferences are fetched raw and merged with BStock defaults locally.
 */

import { coreApiClient } from '@/api/core-client';
import {
  NOTIFICATION_DEFAULTS,
  ALL_NOTIFICATION_TYPES,
} from '@bstock/shared';

import type {
  NotificationsListResponse,
  NotificationsQueryParams,
  NotificationUnreadCount,
  Notification,
  NotificationPreference,
  NotificationPreferencesResponse,
  UpdateNotificationPreferencesInput,
  UpdateNotificationPreferencesResponse,
  ResetNotificationPreferencesResponse,
  NotificationType,
} from '@bstock/shared';

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

const BSTOCK_APP = 'BSTOCK';

/**
 * Get paginated list of notifications
 */
export async function getNotifications(
  params: NotificationsQueryParams = {}
): Promise<NotificationsListResponse> {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', String(params.page));
  if (params.limit) searchParams.set('limit', String(params.limit));
  searchParams.set('app', BSTOCK_APP);

  const response = await coreApiClient.get<ApiResponse<NotificationsListResponse>>(
    `/notifications?${searchParams.toString()}`
  );
  return response.data.data;
}

/**
 * Get unread notifications count
 */
export async function getUnreadCount(): Promise<NotificationUnreadCount> {
  const response = await coreApiClient.get<ApiResponse<NotificationUnreadCount>>(
    `/notifications/unread-count?app=${BSTOCK_APP}`
  );
  return response.data.data;
}

/**
 * Mark a notification as read
 */
export async function markAsRead(id: string): Promise<Notification> {
  const response = await coreApiClient.patch<ApiResponse<Notification>>(
    `/notifications/${id}/read`
  );
  return response.data.data;
}

/**
 * Mark all notifications as read
 */
export async function markAllAsRead(): Promise<{ count: number }> {
  const response = await coreApiClient.patch<ApiResponse<{ count: number }>>(
    `/notifications/read-all?app=${BSTOCK_APP}`
  );
  return response.data.data;
}

/**
 * Get user's notification preferences — merges raw custom prefs with BStock defaults.
 * Core returns only the rows the user has customized; defaults come from @bstock/shared.
 */
export async function getPreferences(): Promise<NotificationPreferencesResponse> {
  const response = await coreApiClient.get<
    ApiResponse<{ preferences: Array<{ type: string; inApp: boolean; email: boolean }> }>
  >('/notifications/preferences');
  const customPrefs = response.data.data.preferences;
  const customMap = new Map(customPrefs.map((p) => [p.type, p]));

  const preferences: NotificationPreference[] = ALL_NOTIFICATION_TYPES.map((type) => {
    const custom = customMap.get(type);
    const defaults = NOTIFICATION_DEFAULTS[type];
    return {
      type: type as NotificationType,
      inApp: custom?.inApp ?? defaults.inApp,
      email: custom?.email ?? defaults.email,
      label: defaults.label,
      category: defaults.category,
    };
  });

  return { preferences };
}

/**
 * Update user's notification preferences
 */
export async function updatePreferences(
  input: UpdateNotificationPreferencesInput
): Promise<UpdateNotificationPreferencesResponse> {
  const response = await coreApiClient.put<ApiResponse<UpdateNotificationPreferencesResponse>>(
    '/notifications/preferences',
    input
  );
  return response.data.data;
}

/**
 * Reset user's notification preferences to defaults
 */
export async function resetPreferences(): Promise<ResetNotificationPreferencesResponse> {
  const response = await coreApiClient.post<ApiResponse<ResetNotificationPreferencesResponse>>(
    '/notifications/preferences/reset'
  );
  return response.data.data;
}
